import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useOrganization } from '@clerk/clerk-react';
import { useApi } from '../lib/api';

interface SessionUser {
  id: string;
  name: string | null;
  email: string;
  avatarUrl: string | null;
}

interface OrgSession {
  id: string;
  title: string;
  status: string;
  startedAt: string;
  endedAt: string | null;
  durationMs: number | null;
  coverageScore: number | null;
  itemsTotal: number | null;
  itemsCovered: number | null;
  user: SessionUser;
}

interface OrgMember {
  id: string;
  name: string | null;
  email: string;
  avatarUrl: string | null;
  role: string;
}

interface Analytics {
  period: { start: string; end: string };
  overall: {
    totalSessions: number;
    avgCoverage: string;
    totalDurationMs: number;
    avgDurationMs: number;
  };
  memberCount: number;
  memberStats: {
    userId: string;
    name: string | null;
    email: string;
    sessionCount: number;
    avgCoverage: string;
    totalDurationMs: number;
  }[];
}

export function OrgSessions() {
  const { organization } = useOrganization();
  const { get } = useApi();

  const [sessions, setSessions] = useState<OrgSession[]>([]);
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [selectedMemberId, setSelectedMemberId] = useState<string>('');

  async function fetchData(page = 1, memberId = '') {
    if (!organization) return;

    try {
      setLoading(true);
      setError(null);

      // Fetch sessions, members, and analytics in parallel
      const [sessionsRes, membersRes, analyticsRes] = await Promise.all([
        get<{ sessions: OrgSession[]; pagination: typeof pagination }>(
          `/organizations/${organization.id}/sessions?page=${page}&limit=20${memberId ? `&memberId=${memberId}` : ''}`
        ),
        get<{ members: OrgMember[] }>(`/organizations/${organization.id}/members`),
        get<Analytics>(`/organizations/${organization.id}/analytics`),
      ]);

      setSessions(sessionsRes.sessions);
      setPagination(sessionsRes.pagination);
      setMembers(membersRes.members);
      setAnalytics(analyticsRes);
    } catch (err) {
      console.error('Error fetching org sessions:', err);
      setError(err instanceof Error ? err.message : 'Failed to load team sessions');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (organization) {
      fetchData(1, selectedMemberId);
    }
  }, [organization, selectedMemberId]);

  function formatDuration(ms: number | null | undefined): string {
    if (!ms) return '-';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    }
    return `${minutes}m ${seconds % 60}s`;
  }

  if (!organization) {
    return (
      <div className="bg-slate-800 rounded-lg p-8 text-center border border-slate-700">
        <p className="text-slate-400">Organization context required</p>
        <p className="mt-2 text-sm text-slate-500">
          Select an organization to view team sessions
        </p>
      </div>
    );
  }

  if (loading && sessions.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4 text-red-400">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Team Sessions</h1>
        <p className="mt-1 text-slate-400">
          View and analyze sessions across your organization
        </p>
      </div>

      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard
            label="Total Sessions"
            value={analytics.overall.totalSessions.toString()}
            subtext="Last 30 days"
          />
          <StatCard
            label="Avg Coverage"
            value={`${analytics.overall.avgCoverage}%`}
            subtext="Across all sessions"
            valueColor={
              parseFloat(analytics.overall.avgCoverage) >= 80
                ? 'text-green-500'
                : parseFloat(analytics.overall.avgCoverage) >= 50
                ? 'text-yellow-500'
                : 'text-red-500'
            }
          />
          <StatCard
            label="Total Time"
            value={formatDuration(analytics.overall.totalDurationMs)}
            subtext="In sessions"
          />
          <StatCard
            label="Team Members"
            value={analytics.memberCount.toString()}
            subtext="Active in org"
          />
        </div>
      )}

      {/* Member Performance Table */}
      {analytics && analytics.memberStats.length > 0 && (
        <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-700">
            <h2 className="text-lg font-medium text-white">Member Performance</h2>
          </div>
          <table className="min-w-full divide-y divide-slate-700">
            <thead className="bg-slate-900/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Member</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Sessions</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Avg Coverage</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Total Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {analytics.memberStats.map((member) => (
                <tr
                  key={member.userId}
                  className="hover:bg-slate-700/50 cursor-pointer"
                  onClick={() => setSelectedMemberId(member.userId)}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-slate-400 mr-3">
                        {(member.name || member.email)[0].toUpperCase()}
                      </div>
                      <div>
                        <p className="text-white font-medium">{member.name || member.email}</p>
                        <p className="text-sm text-slate-500">{member.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-slate-300">{member.sessionCount}</td>
                  <td className="px-6 py-4">
                    <span
                      className={`font-medium ${
                        parseFloat(member.avgCoverage) >= 80
                          ? 'text-green-500'
                          : parseFloat(member.avgCoverage) >= 50
                          ? 'text-yellow-500'
                          : 'text-red-500'
                      }`}
                    >
                      {member.avgCoverage}%
                    </span>
                  </td>
                  <td className="px-6 py-4 text-slate-300">{formatDuration(member.totalDurationMs)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center space-x-4">
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1">Filter by Member</label>
          <select
            value={selectedMemberId}
            onChange={(e) => setSelectedMemberId(e.target.value)}
            className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white text-sm"
          >
            <option value="">All Members</option>
            {members.map((member) => (
              <option key={member.id} value={member.id}>
                {member.name || member.email}
              </option>
            ))}
          </select>
        </div>
        {selectedMemberId && (
          <button
            onClick={() => setSelectedMemberId('')}
            className="mt-6 text-sm text-slate-400 hover:text-white"
          >
            Clear filter
          </button>
        )}
      </div>

      {/* Sessions List */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
          <h2 className="text-lg font-medium text-white">
            Sessions {selectedMemberId && `(${sessions.length})`}
          </h2>
          <span className="text-sm text-slate-400">{pagination.total} total</span>
        </div>

        {sessions.length > 0 ? (
          <table className="min-w-full divide-y divide-slate-700">
            <thead className="bg-slate-900/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Session</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Member</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Duration</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Coverage</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {sessions.map((session) => {
                const date = new Date(session.startedAt);
                const score = Number(session.coverageScore ?? 0);
                const coverageColor =
                  score >= 80 ? 'text-green-500' : score >= 50 ? 'text-yellow-500' : 'text-red-500';

                return (
                  <tr key={session.id} className="hover:bg-slate-700/50">
                    <td className="px-6 py-4">
                      <Link
                        to={`/sessions/${session.id}`}
                        className="text-white hover:text-green-400 font-medium"
                      >
                        {session.title || 'Untitled Session'}
                      </Link>
                      <p className="text-sm text-slate-500">
                        {session.itemsCovered ?? 0}/{session.itemsTotal ?? 0} items
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        {session.user.avatarUrl ? (
                          <img
                            src={session.user.avatarUrl}
                            alt=""
                            className="w-6 h-6 rounded-full mr-2"
                          />
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-slate-700 flex items-center justify-center text-xs text-slate-400 mr-2">
                            {(session.user.name || session.user.email)[0].toUpperCase()}
                          </div>
                        )}
                        <span className="text-slate-300 text-sm">
                          {session.user.name || session.user.email}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-300">
                      {date.toLocaleDateString()}
                      <br />
                      <span className="text-sm text-slate-500">
                        {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-300">{formatDuration(session.durationMs)}</td>
                    <td className="px-6 py-4">
                      <span className={`text-lg font-bold ${coverageColor}`}>{Math.round(score)}%</span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          session.status === 'completed'
                            ? 'bg-green-900/50 text-green-400'
                            : session.status === 'active'
                            ? 'bg-blue-900/50 text-blue-400'
                            : 'bg-slate-700 text-slate-400'
                        }`}
                      >
                        {session.status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="px-6 py-8 text-center">
            <p className="text-slate-400">No sessions found</p>
            {selectedMemberId && (
              <button
                onClick={() => setSelectedMemberId('')}
                className="mt-2 text-green-500 hover:text-green-400 text-sm"
              >
                Clear filter to see all sessions
              </button>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <button
            onClick={() => fetchData(pagination.page - 1, selectedMemberId)}
            disabled={pagination.page === 1}
            className="px-4 py-2 bg-slate-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="text-slate-400">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            onClick={() => fetchData(pagination.page + 1, selectedMemberId)}
            disabled={pagination.page === pagination.totalPages}
            className="px-4 py-2 bg-slate-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  subtext,
  valueColor = 'text-white',
}: {
  label: string;
  value: string;
  subtext: string;
  valueColor?: string;
}) {
  return (
    <div className="bg-slate-800 rounded-lg border border-slate-700 p-4">
      <p className="text-sm text-slate-400">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${valueColor}`}>{value}</p>
      <p className="text-xs text-slate-500 mt-1">{subtext}</p>
    </div>
  );
}
